// src/components/DriverManagement.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  orderBy,
  updateDoc
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// IMPORT MUI
import {
  Box,
  Typography, 
  IconButton,
  CircularProgress, Paper, Button, TextField, List,
  ListItem, ListItemText, ListItemIcon, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports'; 
import EditIcon from '@mui/icons-material/Edit';

// Stile helper (invariato)
const rowStyle = { 
    display: 'flex', 
    flexDirection: { xs: 'column', md: 'row' }, 
    gap: 2.5, 
    mb: 2.5 
};

function DriverManagement({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('driver');
  // --- FINE MODIFICA ---

  // Stati Lista (invariati)
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canManage = userRole === 'admin' || userRole === 'Engineer';
  
  // Stati per il form "Aggiungi" (invariati)
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [peso, setPeso] = useState('');
  const [licenza, setLicenza] = useState('');
  const [note, setNote] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [fiscalCode, setFiscalCode] = useState('');
  const [sdiCode, setSdiCode] = useState('');

  // Stati per il Modale "Modifica" (invariati)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentDriver, setCurrentDriver] = useState(null); 
  const [editFormData, setEditFormData] = useState({
    nomeCompleto: '', peso: '', licenza: '', note: '',
    contactEmail: '', contactPhone: '', address: '', city: '',
    postalCode: '', vatNumber: '', fiscalCode: '', sdiCode: ''
  });

  // useEffect (invariato)
  useEffect(() => {
    if (!teamId) return;
    const driversQuery = query(
      collection(db, "drivers"),
      where("teamId", "==", teamId),
      orderBy("nomeCompleto", "asc")
    );
    const unsubscribe = onSnapshot(driversQuery, (snapshot) => {
      const driversList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDrivers(driversList);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching drivers:", error);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [teamId]);

  // --- MODIFICA: handleAddDriver (con t() per errore) ---
  const handleAddDriver = async (e) => {
    e.preventDefault();
    if (!nomeCompleto || !teamId) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "drivers"), {
        teamId: teamId,
        nomeCompleto: nomeCompleto,
        peso: peso ? parseFloat(peso) : null,
        licenza: licenza,
        note: note,
        contactEmail: contactEmail,
        contactPhone: contactPhone,
        address: address,
        city: city,
        postalCode: postalCode,
        vatNumber: vatNumber,
        fiscalCode: fiscalCode,
        sdiCode: sdiCode
      });
      setNomeCompleto(''); setPeso(''); setLicenza(''); setNote('');
      setContactEmail(''); setContactPhone(''); setAddress(''); setCity('');
      setPostalCode(''); setVatNumber(''); setFiscalCode(''); setSdiCode('');
    } catch (error) {
      console.error("Error adding driver:", error);
      alert(t('add_error')); // Tradotto
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- FINE MODIFICA ---

  // --- MODIFICA: handleDeleteDriver (con t() per conferma/errore) ---
  const handleDeleteDriver = async (driverId) => {
    if (window.confirm(t('delete_confirm'))) { // Tradotto
      try {
        await deleteDoc(doc(db, "drivers", driverId));
      } catch (error) {
        console.error("Error deleting driver:", error);
        alert(t('delete_error')); // Tradotto
      }
    }
  };
  // --- FINE MODIFICA ---

  // --- Funzioni Modale (MODIFICATE con t() per errore) ---
  const handleOpenEditModal = (driver) => {
    setCurrentDriver(driver); 
    setEditFormData({
      nomeCompleto: driver.nomeCompleto || '',
      peso: driver.peso || '',
      licenza: driver.licenza || '',
      note: driver.note || '',
      contactEmail: driver.contactEmail || '',
      contactPhone: driver.contactPhone || '',
      address: driver.address || '',
      city: driver.city || '',
      postalCode: driver.postalCode || '',
      vatNumber: driver.vatNumber || '',
      fiscalCode: driver.fiscalCode || '',
      sdiCode: driver.sdiCode || ''
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentDriver(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateDriver = async (e) => {
    e.preventDefault();
    if (!currentDriver || !editFormData.nomeCompleto) return;
    setIsSubmitting(true);
    const docRef = doc(db, "drivers", currentDriver.id);
    try {
      await updateDoc(docRef, {
        nomeCompleto: editFormData.nomeCompleto,
        peso: editFormData.peso ? parseFloat(editFormData.peso) : null,
        licenza: editFormData.licenza,
        note: editFormData.note,
        contactEmail: editFormData.contactEmail,
        contactPhone: editFormData.contactPhone,
        address: editFormData.address,
        city: editFormData.city,
        postalCode: editFormData.postalCode,
        vatNumber: editFormData.vatNumber,
        fiscalCode: editFormData.fiscalCode,
        sdiCode: editFormData.sdiCode
      });
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating driver:", error);
      alert(t('update_error')); // Tradotto
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- FINE MODIFICA ---


  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} {/* Tradotto */}
        </Typography>
      </Paper>

      {/* 2. Paper Form Aggiungi Pilota */}
      {canManage && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            border: '1px solid', 
            borderColor: 'divider', 
            backgroundColor: 'background.paper' 
          }}
        >
          <Typography variant="h5" gutterBottom>
            {t('add_title')} {/* Tradotto */}
          </Typography>
          <Box component="form" onSubmit={handleAddDriver}>
            
            {/* Sezione 1: Info Base */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, color: 'primary.main' }}>{t('personal_info')}</Typography>
            <Box sx={rowStyle}>
              <TextField name="nomeCompleto" label={t('full_name')} fullWidth value={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.value)} disabled={isSubmitting} required sx={{ flex: 2 }} />
              <TextField name="peso" label={t('weight')} fullWidth type="number" value={peso} onChange={(e) => setPeso(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="licenza" label={t('license')} fullWidth value={licenza} onChange={(e) => setLicenza(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>
            <Box sx={rowStyle}>
              <TextField name="contactEmail" label={t('email')} fullWidth type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="contactPhone" label={t('phone')} fullWidth value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>

            <Divider sx={{ my: 3 }} />
            
            {/* Sezione 2: Info Fatturazione */}
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>{t('billing_details')}</Typography>
            <Box sx={rowStyle}>
              <TextField name="address" label={t('address')} fullWidth value={address} onChange={(e) => setAddress(e.target.value)} disabled={isSubmitting} sx={{ flex: 2 }} />
              <TextField name="city" label={t('city')} fullWidth value={city} onChange={(e) => setCity(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="postalCode" label={t('postal_code')} fullWidth value={postalCode} onChange={(e) => setPostalCode(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>
            <Box sx={rowStyle}>
              <TextField name="vatNumber" label={t('vat_number')} fullWidth value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="fiscalCode" label={t('fiscal_code')} fullWidth value={fiscalCode} onChange={(e) => setFiscalCode(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="sdiCode" label={t('sdi_code')} fullWidth value={sdiCode} onChange={(e) => setSdiCode(e.target.value)} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>
            
            <Divider sx={{ my: 3 }} />

            {/* Sezione 3: Note e Salva */}
            <TextField label={t('notes')} fullWidth multiline rows={3} value={note} onChange={(e) => setNote(e.target.value)} disabled={isSubmitting} />
            
            <Button variant="contained" color="primary" type="submit" disabled={!nomeCompleto || isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} size="large" sx={{ height: '56px', mt: 2.5 }}>
              {t('add_button')}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* 3. Paper Lista Piloti */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Typography variant="h5" gutterBottom>
          {t('existing_title')}
        </Typography>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && drivers.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}> {t('empty')} </Typography> )}
        
        {!isLoading && drivers.length > 0 && (
          <List dense>
            {drivers.map((driver) => (
              <ListItem
                key={driver.id}
                divider
                sx={{ 
                  alignItems: 'flex-start',
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1
                }}
                secondaryAction={
                  canManage ? (
                    <Box>
                      <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditModal(driver)} sx={{ mr: 0.5 }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteDriver(driver.id)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Box>
                  ) : null
                }
              >
                <ListItemIcon sx={{ minWidth: 40, pt: 1.5 }}>
                  <SportsMotorsportsIcon />
                </ListItemIcon>
                {/* ListItemText (Aggiornato con etichette tradotte) */}
                <ListItemText
                  primary={driver.nomeCompleto}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block', fontWeight: 'bold' }}>
                        {t('weight')}: {driver.peso ? `${driver.peso} kg` : 'N/A'}
                      </Typography>
                      {driver.contactEmail && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('email')}: {driver.contactEmail}</Typography>}
                      {driver.contactPhone && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('phone')}: {driver.contactPhone}</Typography>}
                      {driver.vatNumber && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('vat_number')}: {driver.vatNumber}</Typography>}
                      {driver.fiscalCode && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('fiscal_code')}: {driver.fiscalCode}</Typography>}
                      {driver.note && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', pt: 0.5 }}>{t('notes')}: {driver.note}</Typography>}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper> 

      {/* 4. Modale (Dialog) per la Modifica */}
      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('edit_title', { name: currentDriver?.nomeCompleto || '' })}</DialogTitle>
        <Box component="form" onSubmit={handleUpdateDriver}>
          <DialogContent>
            
            {/* Sezione 1: Info Base */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, color: 'primary.main' }}>{t('personal_info')}</Typography>
            <Box sx={rowStyle}>
              <TextField name="nomeCompleto" label={t('full_name')} fullWidth value={editFormData.nomeCompleto} onChange={handleEditFormChange} disabled={isSubmitting} required sx={{ flex: 2 }} />
              <TextField name="peso" label={t('weight')} fullWidth type="number" value={editFormData.peso} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="licenza" label={t('license')} fullWidth value={editFormData.licenza} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>
            <Box sx={rowStyle}>
              <TextField name="contactEmail" label={t('email')} fullWidth type="email" value={editFormData.contactEmail} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="contactPhone" label={t('phone')} fullWidth value={editFormData.contactPhone} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>

            <Divider sx={{ my: 3 }} />
            
            {/* Sezione 2: Info Fatturazione */}
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>{t('billing_details')}</Typography>
            <Box sx={rowStyle}>
              <TextField name="address" label={t('address')} fullWidth value={editFormData.address} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 2 }} />
              <TextField name="city" label={t('city')} fullWidth value={editFormData.city} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="postalCode" label={t('postal_code')} fullWidth value={editFormData.postalCode} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>
            <Box sx={rowStyle}>
              <TextField name="vatNumber" label={t('vat_number')} fullWidth value={editFormData.vatNumber} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="fiscalCode" label={t('fiscal_code')} fullWidth value={editFormData.fiscalCode} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
              <TextField name="sdiCode" label={t('sdi_code')} fullWidth value={editFormData.sdiCode} onChange={handleEditFormChange} disabled={isSubmitting} sx={{ flex: 1 }} />
            </Box>
            
            <Divider sx={{ my: 3 }} />

            {/* Sezione 3: Note */}
            <TextField name="note" label={t('notes')} fullWidth multiline rows={3} value={editFormData.note} onChange={handleEditFormChange} disabled={isSubmitting} />
            
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseEditModal} disabled={isSubmitting}>
              {t('cancel_button')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={!editFormData.nomeCompleto || isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('save_button')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

export default DriverManagement;